/**
 * The cost-programme strip under the expense decision (DESIGN §7): the charge now, the saving in each of the next three years, the payback, and the capital the charge uses, with the drawer that shows the years.
 * Shown only while the picked option is a cut or an investment; the sentence about one-off charges is always with it.
 */
import { useState } from "react";
import { Drawer } from "../../theme/Drawer";
import { ChartFrame } from "../../theme/ChartFrame";
import { formatUsdM } from "../../format";
import { costProgrammeOf, LOOK_THROUGH_CLAUSE, programmeFacts, stripOf } from "./costProgramme";
import { ProgrammeTable } from "./ProgrammeTable";
import type { PreviewResult } from "@bsim/domain";
import "./programme.css";

export function ProgrammeStrip({ result, decisionId, table = true }: { readonly result: PreviewResult | null; readonly decisionId: string; /** False where the driver panel beside it already prints the table and the payback line: only the sentence and the years drawer remain. */ readonly table?: boolean }) {
  const [open, setOpen] = useState(false);
  const p = costProgrammeOf(result), s = stripOf(p);
  return (
    <div className="gx-prog" data-testid={`decide.programme.${decisionId}`}>
      {table ? <><ProgrammeTable programme={p} testId="decide.programme.strip" compact />
      <p className="gx-prog-s gx-prog-pay" data-testid="decide.programme.payback"><b>Pays back in:</b> {s.payback}</p></> : null}
      <p className="gx-prog-s" data-testid="decide.programme.sentence">{s.capital} {LOOK_THROUGH_CLAUSE} <button type="button" className="gx-linkrow" data-testid="decide.programme.show" onClick={() => setOpen(true)}>Show the years</button>
      </p>
      {open ? <Drawer title="Cost programme" onClose={() => setOpen(false)}><ProgrammeYears result={result} /></Drawer> : null}
    </div>
  );
}

/** Charge and savings by year: bars (the charge below the line, each year's saving above it) with the same figures in the chart's own table; the full programme table lives in the driver panel. */
function ProgrammeYears({ result }: { readonly result: PreviewResult | null }) {
  const p = costProgrammeOf(result);
  if (p === undefined) return <p className="gx-muted" data-testid="decide.programme.none">The programme's years appear once the plan has been worked out.</p>;
  const bars = [{ year: "Now", v: -p.chargeUsd }, ...p.savingsByYearUsd.map((v, i) => ({ year: `Year ${i + 1}`, v }))];
  const top = Math.max(...bars.map((b) => Math.abs(b.v)), 1);
  const W = 320, H = 140, mid = H / 2, bw = 44;
  return (
    <div className="gx-prog-d">
      <h3 className="gx-h3">Charge and savings by year</h3>
      <ChartFrame summary="The one-off charge now and the saving kept in each of the next three years" table={{ caption: "Charge and savings by year", head: ["Year", "Amount"], rows: bars.map((b) => [b.year, formatUsdM(b.v, { signed: true })]) }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" aria-hidden="true" focusable="false">
          <line x1="0" x2={W} y1={mid} y2={mid} stroke="var(--border-strong)" />
          {bars.map((b, i) => { const h = (Math.abs(b.v) / top) * (mid - 14), x = 20 + i * 75; return <g key={b.year}><rect x={x} width={bw} y={b.v < 0 ? mid : mid - h} height={h} fill={b.v < 0 ? "var(--crit)" : "var(--good)"} /><text x={x + bw / 2} y={H - 2} textAnchor="middle" fontSize="11" fill="var(--ink-2)">{b.year}</text></g>; })}
        </svg>
      </ChartFrame>
      <dl className="gx-prog-facts gx-num" data-testid="decide.programme.facts">
        {programmeFacts(p).map((f) => <div key={f.id}><dt>{f.label}</dt><dd data-testid={`decide.programme.fact.${f.id}`}>{f.value}</dd></div>)}
      </dl>
      <p className="gx-small gx-muted">{p.lookThroughLine}</p>
      <p className="gx-small gx-faint">The saving shown is what stays after part of it creeps back; the cost of bankers who leave and of fee drag is taken off in the net row, and the payback is worked out on that row.</p>
    </div>
  );
}
